import clsx from "clsx";

import type { ILink } from "@Components/Link";
import { Link } from "@Components/Link";

import * as css from "./SvgIconLink.module.css";

export type ISvgIconLink = ILink & {
    svgAppearance?: SvgAppearance;
};

export enum SvgAppearance {
    DarkBlue,
    Magenta,
    White,
    Gray,
}

export function svgClassName(className?: SvgAppearance): string {
    switch (className) {
        case SvgAppearance.Magenta:
            return css.magenta;
        case SvgAppearance.White:
            return css.white;
        case SvgAppearance.Gray:
            return css.gray;
        default:
            return css.darkBlue;
    }
}

export abstract class SvgIconLink<TProps extends ISvgIconLink = ISvgIconLink> extends Link<TProps> {
    protected abstract get svgIcon(): JSX.Element;

    private get svg(): JSX.Element | null {
        const svg = this.svgIcon;
        if (svg === null) {
            return null;
        }

        return {
            ...svg,
            props: {
                ...svg.props,
                className: clsx(css.icon, svgClassName(this.props.svgAppearance), svg.props.className),
            },
        };
    }

    public static readonly defaultProps: ISvgIconLink = {
        ...Link.defaultProps,
        svgAppearance: SvgAppearance.DarkBlue,
    };

    protected cssClassName: string | null;

    protected isPureComponent = true;

    protected rootDefaultAttributes(...className: string[]) {
        const classes = this.cssClassName ? [...className, this.cssClassName] : className;
        return super.rootDefaultAttributes(css.svgIconLinkComponent, ...classes);
    }

    protected renderContent(): JSX.Element {
        const getText = () => {
            if (!this.text) {
                return null;
            }

            return <span className={css.text}>{this.text}</span>;
        };

        return (
            <div className={css.linkContainer}>
                {this.svg}
                {getText()}
            </div>
        );
    }
}
